import { useAuth } from "@/hooks/useAuth.ts";
import { Dashboard } from "@/components/pages/dashboard.tsx";
import { Profile } from "@/components/pages/profile.tsx";
import { ThemeProvider } from "@/components/theme-provider.tsx";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient.ts";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/ui/toaster.tsx";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./lib/dayjsExt.ts";
import { useMarketplaceProducts } from "./hooks/useMarketplaceProducts.ts";
import { useFirebaseLayouts } from "./hooks/useFirebaseLayouts.ts";
import LiveChat from "./components/shared/liveChat.tsx";
import { Login } from "./components/auth/Login.tsx";
import { captureReferralParams } from "./lib/utils/referral.ts";
import { useEffect } from "react";
import { useUpdateActivity } from "./hooks/useUpdateActivity.ts";
import { Analytics } from "@vercel/analytics/react";
import { GlobalNatsPositionStatusListener } from "@/components/core/GlobalNatsOrderErrorListener";
import { useServiceWorkerUpdater } from "./hooks/useServiceWorkerUpdater.tsx";
import { ServiceWorkerDebugPanel } from "./components/dev/ServiceWorkerDebugPanel.tsx";

// Wrapper component to handle protected routes
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    // Preserve the current URL including query parameters in the state
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}

// Login route component to handle return URL
function LoginRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl") || "/";

  if (user) {
    return <Navigate to={returnUrl} replace />;
  }

  return <Login />;
}

function Layout() {
  const { user, login, isLoading } = useAuth();
  useMarketplaceProducts();
  useFirebaseLayouts(user || undefined);
  useUpdateActivity();

  if (isLoading) {
    return <div className="animate-pulse">Loading user...</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-78px)]">
      <div className="flex-1">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster />
          <LiveChat />
          <Analytics />
        </BrowserRouter>
      </div>
    </div>
  );
}

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

function App() {
  const isPersistent = window.localStorage.getItem("isPersistent") === "true";

  // Use the service worker updater hook
  useServiceWorkerUpdater();

  // Capture referral parameters on app load
  useEffect(() => {
    captureReferralParams();
  }, []);

  useEffect(() => {
    document.addEventListener("resume", () => {
      console.log("resume");
      window.location.reload();
    });
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <GlobalNatsPositionStatusListener />
      {isPersistent ? (
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister: localStoragePersister,
          }}
        >
          <Layout />
        </PersistQueryClientProvider>
      ) : (
        <QueryClientProvider client={queryClient}>
          <Layout />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      )}
    </ThemeProvider>
  );
}

export default App;
